/**
Project: Voice Panel (c)
Title: General Actions 
Description: General actions to handle page wait dialog box
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/

import axios from "axios";
import { AppConfigProps } from "../../../../core/settings/app-config";
import {
  logMessage,
  checkIdentitySession,
  getSessionExpiredError,
} from "../../../../core/actions/common-actions";
import {
  PAGE_WAIT_DIALOG_DATA,
  COUNTRY_STATES_DATA,
  COMMUNICATION_PLATFORMS_DATA,
  USER_CONSENT_DATA
} from "../../../../store/voice-admin/constants";
import IntlUtl from "../../../../core/helpers/intl-util";

const actionFileName = "general-actions.js";

// Set Page Wait Dialog Display Status true (show Dialog)
export function showPageWaitDialog(labelText, labelPosition, spinnerSize) {
  return async function (dispatch) {
    try {
      if (!spinnerSize) spinnerSize = "medium";
      if (!labelText)
        labelText = IntlUtl.getText("common", "content.loadingInprogress");
      if (!labelPosition) {
        labelPosition = "below";
      }
      const data = {
        displayStatus: true,
        spinnerSize: spinnerSize,
        labelText: labelText,
        labelPosition: labelPosition,
      };

      dispatch({
        type: PAGE_WAIT_DIALOG_DATA,
        payload: data,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

// Set Page Wait Dialog Display Status false (Hide Dialog)
export function hidePageWaitDialog() {
  return async function (dispatch) {
    try {
      const data = {
        displayStatus: false,
        spinnerSize: "medium",
        labelText: "",
        labelPosition: "below",
      };
      dispatch({
        type: PAGE_WAIT_DIALOG_DATA,
        payload: data,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function getCountryStates(cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix + "/general/country-states",
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getCountryData()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function setCountryStates(countryStatesData) {
  return async function (dispatch) {
    try {
      dispatch({
        type: COUNTRY_STATES_DATA,
        payload: countryStatesData,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function getCountryList(countryList, displayParams) {
  let countries = [];
  if (countryList) {
    if (displayParams && displayParams.showSippioSupportCountries === true) {
      countryList.forEach((country) => {
        if (country.sippioSupport === "Yes") {
          countries.push(country.countryName);
        }
      });
    } else {
      countryList.forEach((country) => {
        countries.push(country.countryName);
      });
    }
  }

  return countries;
}

export function isNullOrEmpty(value) {
  return (
    !value ||
    value === undefined ||
    value === null ||
    value === "" ||
    value.length === 0
  );
}

export function isNotNullOrNotEmpty(value) {
  return (
    !value ||
    value !== undefined ||
    value !== null ||
    value !== "" ||
    value.length > 0
  );
}

export function capitalizeFirstLetter(fieldValue) {
  let fieldName = "";
  if (fieldValue && fieldValue.length > 0) {
    fieldName =
      fieldValue.charAt(0).toUpperCase() + fieldValue.slice(1).toLowerCase();
  }
  return fieldName;
}

export function checkNumberUserPrivileges(subscriberAccount) {
  let accountNumber =
    process.env.REACT_APP_NUMBERS_PRIVILEGE_ACCOUNT_NUMBERS.split(",");
  accountNumber = accountNumber.filter((rec) => rec !== "");
  let status = true;
  accountNumber.forEach((rec) => {
    if (subscriberAccount.startsWith(rec) === true) {
      status = false;
      return;
    }
  });
  return status;
}
export function getCommunicationPlatforms(cancelToken) {
  return new Promise(function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      axios
        .get(
          AppConfigProps.serverRoutePrefix +
          "/general/communication-platforms",
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getCommunicationPlatforms()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}
export function setCommunicationPlatforms(CommunicationPlatformsAllRecords) {
  return async function (dispatch) {
    try {
      dispatch({
        type: COMMUNICATION_PLATFORMS_DATA,
        payload: CommunicationPlatformsAllRecords,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}


export function handleCommunicationPlatformName(platform){
  return platform?.platformDescription??'';
  }

  export function getAppConsent(cancelToken) {
    return new Promise(function (resolve, reject) {
      let identitySession = checkIdentitySession();
      if (identitySession === true) {
        axios
          .get(
            AppConfigProps.serverRoutePrefix +
            "/general/app-consent",
            { headers: { AppUrl: window.location.origin } },
            cancelToken
          )
          .then((res) => {
            resolve(res);
          })
          .catch((err) => {
            if (axios.isCancel(err)) {
              logMessage(
                AppConfigProps.log.severityLevel.warning,
                actionFileName + ">getAppConsent()",
                AppConfigProps.httpStatusCode.clientClosedRequest,
                err.message
              );
            } else {
              reject(err.response);
            }
          });
      } else {
        reject(getSessionExpiredError());
      }
    });
  }

  export function acceptAppConsent(consentObject,cancelToken) {
    return new Promise(function (resolve, reject) {
      let identitySession = checkIdentitySession();
      if (identitySession === true) {
        axios
          .post(
            AppConfigProps.serverRoutePrefix +
            "/general/app-consent",consentObject,
            { headers: { AppUrl: window.location.origin } },
            cancelToken
          )
          .then((res) => {
            resolve(res);
          })
          .catch((err) => {
            if (axios.isCancel(err)) {
              logMessage(
                AppConfigProps.log.severityLevel.warning,
                actionFileName + ">acceptAppConsent()",
                AppConfigProps.httpStatusCode.clientClosedRequest,
                err.message
              );
            } else {
              reject(err.response);
            }
          });
      } else {
        reject(getSessionExpiredError());
      }
    });
  }

  export function withdrawAppConsent(consentUserId,consentObject,cancelToken) {
    return new Promise(function (resolve, reject) {
      let identitySession = checkIdentitySession();
      if (identitySession === true) {
        axios
          .put(
            AppConfigProps.serverRoutePrefix +
            `/general/app-consent/${consentUserId}`,consentObject,
            { headers: { AppUrl: window.location.origin } },
            cancelToken
          )
          .then((res) => {
            resolve(res);
          })
          .catch((err) => {
            if (axios.isCancel(err)) {
              logMessage(
                AppConfigProps.log.severityLevel.warning,
                actionFileName + ">acceptAppConsent()",
                AppConfigProps.httpStatusCode.clientClosedRequest,
                err.message
              );
            } else {
              reject(err.response);
            }
          });
      } else {
        reject(getSessionExpiredError());
      }
    });
  }

  export function setUserConsentRecord(userConsentData) {
    return async function (dispatch) {
      try {
        dispatch({
          type: USER_CONSENT_DATA,
          payload: userConsentData,
        });
      } catch (err) {
        throw Error(err);
      }
    };
  }

  export function getTenantAccountsNumberSyncNow(cancelToken) {
    return new Promise(async function (resolve, reject) {
      let identitySession = checkIdentitySession();
      if (identitySession === true) {
        await axios
          .get(
            AppConfigProps.serverRoutePrefix + `/tenant-accounts/numbers/sync`,
            cancelToken
          )
          .then((res) => {
            resolve(res);
          })
          .catch((err) => {
            if (axios.isCancel(err)) {
              logMessage(
                AppConfigProps.log.severityLevel.warning,
                actionFileName + ">getTenantAccountsNumberSyncNow()",
                AppConfigProps.httpStatusCode.clientClosedRequest,
                err.message
              );
            } else {
              reject(err.response);
            }
          });
      } else {
        reject(getSessionExpiredError());
      }
    });
  }

  export function tenentAccountNumberSyncNow(userData, cancelToken) {
    return new Promise(async function (resolve, reject) {
      let identitySession = checkIdentitySession();
      if (identitySession === true) {
        await axios
          .post(
            AppConfigProps.serverRoutePrefix + "/tenant-accounts/numbers/sync",
            userData,
            cancelToken
          )
          .then((res) => {
            resolve(res);
          })
          .catch((err) => {
            if (axios.isCancel(err)) {
              logMessage(
                AppConfigProps.log.severityLevel.warning,
                actionFileName + ">tenentAccountNumberSyncNow()",
                AppConfigProps.httpStatusCode.clientClosedRequest,
                err.message
              );
            } else {
              reject(err.response);
            }
          });
      } else {
        reject(getSessionExpiredError());
      }
    });
  }